import { useState } from "react";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

const TEST_OTP = "1992";

export type PublicVerificationTheme = "casino" | "mystery";

export interface PublicMobileVerificationProps {
  theme: PublicVerificationTheme;
  onVerifiedChange?: (verified: boolean) => void;
  className?: string;
}

const themeStyles: Record<
  PublicVerificationTheme,
  {
    card: string;
    title: string;
    label: string;
    input: string;
    button: string;
    buttonOutline: string;
    success: string;
  }
> = {
  casino: {
    card:
      "border-amber-500/30 bg-gradient-to-br from-[#12081f]/95 via-[#1a0f2e]/90 to-[#0c0614]/95 text-white shadow-[0_0_40px_rgba(168,85,247,0.15)] backdrop-blur-xl",
    title: "text-amber-100",
    label: "text-purple-200/80",
    input:
      "border-purple-500/30 bg-black/40 text-white placeholder:text-purple-300/40 focus-visible:ring-amber-400/50",
    button:
      "bg-gradient-to-r from-amber-500 to-amber-600 text-purple-950 hover:from-amber-400 hover:to-amber-500",
    buttonOutline:
      "border-amber-500/40 bg-transparent text-amber-200 hover:bg-amber-500/10 hover:text-amber-100",
    success: "text-emerald-400",
  },
  mystery: {
    card:
      "border-purple-500/30 bg-gradient-to-br from-[#0f172a]/95 via-[#1e1b4b]/90 to-[#030712]/95 text-white shadow-[0_0_40px_rgba(59,130,246,0.12)] backdrop-blur-xl",
    title: "text-purple-100",
    label: "text-blue-200/80",
    input:
      "border-blue-500/30 bg-black/40 text-white placeholder:text-blue-300/40 focus-visible:ring-purple-400/50",
    button:
      "bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-500 hover:to-blue-500",
    buttonOutline:
      "border-purple-500/40 bg-transparent text-purple-200 hover:bg-purple-500/10 hover:text-purple-100",
    success: "text-emerald-400",
  },
};

export function PublicMobileVerification({
  theme,
  onVerifiedChange,
  className,
}: PublicMobileVerificationProps) {
  const { toast } = useToast();
  const [mobile, setMobile] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const styles = themeStyles[theme];

  const handleSendOtp = () => {
    if (!mobile || mobile.length < 7 || mobile.length > 15) {
      toast({
        title: "Invalid mobile number",
        description: "Please enter a valid mobile number (7-15 digits).",
        variant: "destructive",
      });
      return;
    }
    setOtpSent(true);
    setIsVerified(false);
    onVerifiedChange?.(false);
    setOtp("");
    toast({
      title: "OTP sent",
      description: "An OTP has been sent to the entered mobile number.",
    });
  };

  const handleVerifyOtp = () => {
    if (!otp || otp.length < 4) {
      toast({
        title: "Invalid OTP",
        description: "Please enter a valid OTP.",
        variant: "destructive",
      });
      return;
    }
    if (otp !== TEST_OTP) {
      toast({
        title: "Incorrect OTP",
        description: "Please enter the correct OTP.",
        variant: "destructive",
      });
      setIsVerified(false);
      onVerifiedChange?.(false);
      return;
    }
    setIsVerified(true);
    onVerifiedChange?.(true);
    toast({
      title: "OTP verified",
      description: "You're all set — start playing!",
    });
  };

  return (
    <Card className={cn(styles.card, className)}>
      <CardHeader className="pb-3">
        <CardTitle
          className={cn("flex items-center gap-2 text-lg", styles.title)}
        >
          <ShieldCheck className="h-5 w-5 text-amber-400" />
          Mobile Verification
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <label className={cn("text-sm font-medium", styles.label)}>
            Mobile Number
          </label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              type="tel"
              placeholder="Enter mobile number"
              value={mobile}
              onChange={(e) => {
                setMobile(e.target.value.replace(/\D/g, ""));
                if (isVerified) {
                  setIsVerified(false);
                  onVerifiedChange?.(false);
                }
              }}
              maxLength={15}
              disabled={isVerified}
              className={styles.input}
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleSendOtp}
              disabled={isVerified}
              className={cn("shrink-0", styles.buttonOutline)}
            >
              Send OTP
            </Button>
          </div>
        </div>

        {otpSent && !isVerified && (
          <div className="space-y-2">
            <label className={cn("text-sm font-medium", styles.label)}>
              Enter OTP
            </label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                type="tel"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                maxLength={6}
                className={styles.input}
              />
              <Button
                type="button"
                onClick={handleVerifyOtp}
                className={cn("shrink-0", styles.button)}
              >
                Verify
              </Button>
            </div>
          </div>
        )}

        {isVerified && (
          <p className={cn("text-xs font-medium", styles.success)}>
            ✓ Mobile verified — you can play now.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
